import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { DollarSign, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight, PieChart as PieChartIcon } from 'lucide-react';

// --- Dummy Data ---

// --- Components ---

const CurrencyFormatter = ({ value }: { value: number }) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const StatCard = ({ title, value, change, trend, icon: Icon, subtext }: any) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-4">
            <div>
                <p className="text-sm text-slate-500 font-bold uppercase tracking-wide mb-1 opacity-70">{title}</p>
                <h3 className="text-3xl font-bold text-slate-900">{value}</h3>
            </div>
            <div className={`p-3 rounded-xl ${trend === 'up' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                <Icon size={24} strokeWidth={2} />
            </div>
        </div>
        <div className="flex items-center justify-between">
            <div className={`flex items-center text-sm font-bold ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trend === 'up' ? <ArrowUpRight size={18} className="mr-1" /> : <ArrowDownRight size={18} className="mr-1" />}
                {change}
            </div>
            <span className="text-slate-400 text-xs font-medium">{subtext}</span>
        </div>
    </div>
);

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl border border-slate-800 text-xs">
                <p className="font-bold mb-2 text-slate-300 uppercase tracking-wider">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></div>
                        <span className="text-slate-400 capitalize">{entry.name}:</span>
                        <span className="font-bold font-mono">{CurrencyFormatter({ value: entry.value })}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

export const BIFinancialView = () => {
    const { t } = useTranslation('bi');
    const [timeRange, setTimeRange] = useState('1Y');

    const monthlyData = useMemo(() => [
        { name: 'Jan', revenue: 45000, expenses: 32000, profit: 13000, cashFlow: 5000 },
        { name: 'Feb', revenue: 52000, expenses: 34000, profit: 18000, cashFlow: 8000 },
        { name: 'Mar', revenue: 48000, expenses: 31000, profit: 17000, cashFlow: 7500 },
        { name: 'Apr', revenue: 61000, expenses: 38000, profit: 23000, cashFlow: 12000 },
        { name: 'May', revenue: 58000, expenses: 36000, profit: 22000, cashFlow: 11000 },
        { name: 'Jun', revenue: 68000, expenses: 42000, profit: 26000, cashFlow: 15000 },
        { name: 'Jul', revenue: 75000, expenses: 45000, profit: 30000, cashFlow: 18000 },
        { name: 'Aug', revenue: 72000, expenses: 44000, profit: 28000, cashFlow: 16000 },
        { name: 'Sep', revenue: 85000, expenses: 50000, profit: 35000, cashFlow: 22000 },
        { name: 'Oct', revenue: 92000, expenses: 55000, profit: 37000, cashFlow: 25000 },
        { name: 'Nov', revenue: 88000, expenses: 52000, profit: 36000, cashFlow: 23000 },
        { name: 'Dec', revenue: 98000, expenses: 58000, profit: 40000, cashFlow: 28000 },
    ], []);

    const projectedData = useMemo(() => [
        { name: 'Q1 2026', revenue: 110000, expenses: 65000, profit: 45000 },
        { name: 'Q2 2026', revenue: 125000, expenses: 72000, profit: 53000 },
        { name: 'Q3 2026', revenue: 140000, expenses: 80000, profit: 60000 },
        { name: 'Q4 2026', revenue: 160000, expenses: 90000, profit: 70000 },
    ], []);

    const expenseBreakdown = useMemo(() => [
        { name: t('categories.salaries'), value: 45000, color: '#0f172a' },
        { name: t('categories.marketing'), value: 25000, color: '#3b82f6' },
        { name: t('categories.operations'), value: 15000, color: '#f59e0b' },
        { name: t('categories.software'), value: 10000, color: '#10b981' },
        { name: t('categories.office'), value: 5000, color: '#6366f1' },
    ], [t]);

    return (
        <div className="space-y-8 animate-fadeIn pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <PieChartIcon className="text-blue-600" />
                        {t('header.title')}
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">{t('header.subtitle')}</p>
                </div>
                <div className="flex bg-slate-100 p-1 rounded-lg w-fit">
                    {['1Q', '2Q', '1Y', '5Y'].map((range) => (
                        <button
                            key={range}
                            onClick={() => setTimeRange(range)}
                            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${timeRange === range
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            {t(`ranges.${range}`)}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title={t('kpi.revenue')}
                    value="$1,245,000"
                    change="+12.5%"
                    trend="up"
                    icon={TrendingUp}
                    subtext={t('kpi.revenue_sub')}
                />
                <StatCard
                    title={t('kpi.profit')}
                    value="$384,200"
                    change="+8.2%"
                    trend="up"
                    icon={DollarSign}
                    subtext={t('kpi.profit_sub', { value: '30.8%' })}
                />
                <StatCard
                    title={t('kpi.cash')}
                    value="$142,500"
                    change="-2.4%"
                    trend="down"
                    icon={Wallet}
                    subtext={t('kpi.cash_sub', { months: 14 })}
                />
                <StatCard
                    title={t('kpi.burn')}
                    value="$28,400"
                    change="+1.1%"
                    trend="down"
                    icon={TrendingDown} // Burn going UP is technically bad/neutral usually shown as red/warning or just indicator
                    subtext={t('kpi.burn_sub')}
                />
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Revenue Chart */}
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="font-bold text-slate-800">{t('charts.revenue_expense_title')}</h3>
                        <div className="flex items-center gap-4 text-xs font-medium">
                            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500"></div> {t('charts.revenue_label')}</div>
                            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-slate-300"></div> {t('charts.expenses_label')}</div>
                        </div>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(value) => `$${value / 1000}k`} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9' }} />
                                <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                                <Area type="monotone" dataKey="expenses" stroke="#94a3b8" strokeWidth={2} fillOpacity={0} fill="transparent" strokeDasharray="5 5" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Expense Breakdown */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col">
                    <h3 className="font-bold text-slate-800 mb-6">{t('charts.expense_structure_title')}</h3>
                    <div className="flex-1 min-h-[250px] relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={expenseBreakdown}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {expenseBreakdown.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    iconType="circle"
                                    iconSize={8}
                                    wrapperStyle={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        {/* Center Text */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none pb-8">
                            <div className="text-center">
                                <p className="text-xs text-slate-400 font-bold uppercase">{t('charts.total')}</p>
                                <p className="text-xl font-bold text-slate-900">$100k</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Cash Flow */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-6">{t('charts.cashflow_title')}</h3>
                    <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(value) => `$${value / 1000}k`} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                                <Bar dataKey="cashFlow" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Quarterly Forecast */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-6">{t('charts.quarterly_title', { year: '2026' })}</h3>
                    <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={projectedData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(value) => `$${value / 1000}k`} />
                                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#334155', fontWeight: 600 }} width={60} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                                <Bar dataKey="profit" fill="#6366f1" radius={[0, 4, 4, 0] as [number, number, number, number]} barSize={24} background={{ fill: '#f1f5f9', radius: [0, 4, 4, 0] as [number, number, number, number] }} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Detailed Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h3 className="font-bold text-slate-800">{t('table.title')}</h3>
                    <button className="text-blue-600 text-sm font-bold hover:underline">{t('table.export')}</button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-6 py-4">{t('table.headers.month')}</th>
                                <th className="px-6 py-4 text-right">{t('table.headers.revenue')}</th>
                                <th className="px-6 py-4 text-right">{t('table.headers.expenses')}</th>
                                <th className="px-6 py-4 text-right">{t('table.headers.profit')}</th>
                                <th className="px-6 py-4 text-right">{t('table.headers.cashflow')}</th>
                                <th className="px-6 py-4 text-center">{t('table.headers.margin')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {monthlyData.map((row, index) => (
                                <tr key={index} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 font-bold text-slate-900">{row.name}</td>
                                    <td className="px-6 py-4 text-right font-medium text-slate-700">{CurrencyFormatter({ value: row.revenue })}</td>
                                    <td className="px-6 py-4 text-right text-slate-500">{CurrencyFormatter({ value: row.expenses })}</td>
                                    <td className="px-6 py-4 text-right font-bold text-green-600">+{CurrencyFormatter({ value: row.profit })}</td>
                                    <td className="px-6 py-4 text-right text-slate-700">{CurrencyFormatter({ value: row.cashFlow })}</td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${row.profit / row.revenue > 0.3 ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                            {Math.round((row.profit / row.revenue) * 100)}%
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};
